
import { useState, useEffect, useRef } from "react";
import {
  getInvitationByToken,
  markInvitationAsViewed
} from "@/services/userInvitationService";
import { validateTokenFormat } from "@/services/tokenService";
import { UserInvitation } from "@/types/userInvitation";
import { resolveInvitationStatus } from "@/utils/invitationExpiry";
import welcomePageCache from "@/utils/welcomePageCache";
import { useFirestoreError } from "@/hooks/useFirestoreError";

export enum TokenStatus {
  LOADING,
  INVALID_TOKEN,
  EXPIRED_TOKEN,
  ALREADY_ACCEPTED,
  VALID_TOKEN
}

export const useInvitationToken = (tokenFromUrl: string | null) => {
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>(TokenStatus.LOADING);
  const [invitation, setInvitation] = useState<UserInvitation | null>(null);
  const [hasUpdatedViewedTime, setHasUpdatedViewedTime] = useState(false);
  const { handleFirestoreError } = useFirestoreError();
  
  // Refs to track initialization status
  const hasInitializedToken = useRef(false);

  useEffect(() => {
    // Skip if we've already initialized the token
    if (hasInitializedToken.current || !tokenFromUrl) return;
    
    console.log("useInvitationToken: Initializing token validation for:", tokenFromUrl);
    
    const validateToken = async () => {
      try {
        // Check if we already have cached validation result
        const cachedValidation = welcomePageCache.getCachedTokenValidation(tokenFromUrl);
        
        if (cachedValidation) {
          console.log("useInvitationToken: Using cached validation result:", 
            cachedValidation.isValid ? "valid" : "invalid");
          
          // Use cached result
          if (!cachedValidation.isValid) {
            setTokenStatus(TokenStatus.INVALID_TOKEN);
            return;
          }
          
          if (cachedValidation.invitation) {
            const inv = cachedValidation.invitation;
            console.log("useInvitationToken: Cached invitation found:", inv.id, "Status:", inv.status, "Type:", inv.type);
            setInvitation(inv);

            const cachedStatus = resolveInvitationStatus(inv);
            if (cachedStatus === 'expired') {
              setTokenStatus(TokenStatus.EXPIRED_TOKEN);
            } else if (cachedStatus === 'accepted') {
              setTokenStatus(TokenStatus.ALREADY_ACCEPTED);
            } else {
              setTokenStatus(TokenStatus.VALID_TOKEN);
            }
            
            hasInitializedToken.current = true;
            return;
          }
        }
        
        // Validate token format (without DB read)
        if (!validateTokenFormat(tokenFromUrl)) {
          console.log("useInvitationToken: Token format invalid");
          setTokenStatus(TokenStatus.INVALID_TOKEN);
          welcomePageCache.cacheTokenValidation(tokenFromUrl, false, null);
          hasInitializedToken.current = true;
          return;
        }
        
        console.log("useInvitationToken: Token format valid");

        // Get invitation by token - this is the main database read
        console.log("useInvitationToken: Fetching invitation from database");
        let invData;
        try {
          invData = await getInvitationByToken(tokenFromUrl);
        } catch (error) {
          console.error("useInvitationToken: Error getting invitation:", error);
          handleFirestoreError(error);
          setTokenStatus(TokenStatus.INVALID_TOKEN);
          welcomePageCache.cacheTokenValidation(tokenFromUrl, false, null);
          hasInitializedToken.current = true;
          return;
        }
        
        if (!invData) {
          console.log("useInvitationToken: No invitation found with this token");
          setTokenStatus(TokenStatus.INVALID_TOKEN);
          welcomePageCache.cacheTokenValidation(tokenFromUrl, false, null);
          hasInitializedToken.current = true;
          return;
        }
        
        // Cache the result
        console.log("useInvitationToken: Valid invitation found:", invData.id, 
          "Status:", invData.status, "Type:", invData.type,
          "Organization:", invData.organization?.id);
        welcomePageCache.cacheTokenValidation(tokenFromUrl, true, invData);
        
        setInvitation(invData);
        
        // Check invitation status
        const effectiveStatus = resolveInvitationStatus(invData);
        if (effectiveStatus === 'expired') {
          console.log("useInvitationToken: Invitation has expired");
          setTokenStatus(TokenStatus.EXPIRED_TOKEN);
        } else if (effectiveStatus === 'accepted') {
          console.log("useInvitationToken: Invitation has already been accepted");
          setTokenStatus(TokenStatus.ALREADY_ACCEPTED);
        } else {
          console.log("useInvitationToken: Valid invitation with status:", invData.status);
          setTokenStatus(TokenStatus.VALID_TOKEN);
        }
        
        hasInitializedToken.current = true;
      } catch (error) {
        console.error("useInvitationToken: Unexpected error during token validation:", error);
        handleFirestoreError(error);
        setTokenStatus(TokenStatus.INVALID_TOKEN);
      }
    };
    
    validateToken();
  }, [tokenFromUrl, handleFirestoreError]);
  
  // Handle updating view time as a separate effect to avoid unnecessary re-renders
  useEffect(() => {
    // Only update view time if token is valid and hasn't been updated yet
    if (tokenStatus === TokenStatus.VALID_TOKEN && !hasUpdatedViewedTime && tokenFromUrl && invitation) {
      console.log("useInvitationToken: Updating invitation view time and incrementing view count");
      
      const updateViewTime = async () => {
        try {
          const updated = await markInvitationAsViewed(tokenFromUrl);
          if (updated) {
            console.log("useInvitationToken: Successfully updated view time and incremented view count");
            setHasUpdatedViewedTime(true);
          } else {
            console.log("useInvitationToken: Failed to update view time and view count");
          }
        } catch (error) {
          console.error("useInvitationToken: Error updating view time and view count:", error);
          handleFirestoreError(error);
        }
      };
      
      updateViewTime();
    }
  }, [tokenStatus, hasUpdatedViewedTime, tokenFromUrl, invitation, handleFirestoreError]);
  
  return {
    tokenStatus,
    invitation,
    hasUpdatedViewedTime
  };
};
